package com.yendo.mx.ui.profile

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage
import com.yendo.mx.models.PedidoFirebase
import com.yendo.mx.ui.theme.RosaYendo
import com.yendo.mx.utils.formatFecha
import com.yendo.mx.utils.formatPrecio
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun DetallePedidoScreen(
    pedidoId: String,
    estado: String,
    onBack: () -> Unit
) {
    // 🚀 COLORES DARK MODE
    val isDark = isSystemInDarkTheme()
    val colorFondoGlobal = if (isDark) Color(0xFF121212) else Color(0xFFF8F8F8)
    val colorFondoTarjetas = if (isDark) Color(0xFF1E1E1E) else Color.White
    val colorTextoPrincipal = if (isDark) Color.White else Color.Black
    val colorTextoSecundario = if (isDark) Color(0xFFAAAAAA) else Color.Gray
    val colorDivisor = if (isDark) Color(0xFF2C2C2E) else Color(0xFFF0F0F0)

    // 🚀 ESTADO DE CARGA Y DATOS REALES
    var pedido by remember { mutableStateOf<PedidoFirebase?>(null) }
    var cargando by remember { mutableStateOf(true) }

    LaunchedEffect(pedidoId) {
        try {
            val doc = Firebase.firestore.collection("Pedidos").document(pedidoId).get()
            if (doc.exists) {
                pedido = doc.data<PedidoFirebase>()
            }
        } catch (e: Exception) {
            println("❌ Error al cargar detalle: ${e.message}")
        } finally {
            cargando = false
        }
    }

    // LÓGICA DE ESTADO VISUAL
    val estadoUpper = pedido?.estadoActual?.uppercase() ?: estado.uppercase()
    val (colorEstado, iconoEstado, mensajeEstado) = when (estadoUpper) {
        "ENTREGADO", "CONCLUIDO", "COMPLETADO" -> Triple(Color(0xFF00C853), Icons.Default.CheckCircle, "Pedido entregado con éxito")
        "CANCELADO", "RECHAZADO" -> Triple(Color(0xFFD50000), Icons.Default.Cancel, "Este pedido fue cancelado")
        "EN_CAMINO" -> Triple(RosaYendo, Icons.Default.TwoWheeler, "El repartidor va hacia ti")
        else -> Triple(Color(0xFFFFAB00), Icons.Default.Storefront, "El restaurante está preparando tu pedido")
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = {
                    // 🚀 Mostramos el Folio (idCorto) si existe, si no, recortamos el ID
                    val folio = pedido?.idCorto?.takeIf { it.isNotBlank() } ?: pedidoId.takeLast(6).uppercase()
                    Text("Pedido #$folio", fontWeight = FontWeight.ExtraBold, color = colorTextoPrincipal)
                },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Atrás", tint = colorTextoPrincipal)
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(containerColor = colorFondoGlobal)
            )
        },
        containerColor = colorFondoGlobal
    ) { padding ->
        if (cargando) {
            Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = RosaYendo)
            }
        } else if (pedido == null) {
            Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                Text("No se encontró la información del pedido", color = colorTextoSecundario)
            }
        } else {
            val p = pedido ?: return@Scaffold
            Column(
                modifier = Modifier.fillMaxSize().padding(padding).verticalScroll(rememberScrollState())
            ) {
                // --- BANNER DE ESTADO ---
                Surface(color = colorEstado.copy(alpha = 0.1f), modifier = Modifier.fillMaxWidth()) {
                    Row(modifier = Modifier.padding(16.dp), verticalAlignment = Alignment.CenterVertically) {
                        Icon(iconoEstado, null, tint = colorEstado, modifier = Modifier.size(28.dp))
                        Spacer(Modifier.width(12.dp))
                        Column {
                            Text(estadoUpper, fontWeight = FontWeight.Black, color = colorEstado, fontSize = 14.sp)
                            Text(mensajeEstado, color = colorTextoSecundario, fontSize = 13.sp)

                            // 🚀 MOTIVO DE CANCELACIÓN (SI EL PEDIDO FUE ANULADO)
                            val esCancelado = estadoUpper in listOf("CANCELADO", "RECHAZADO")
                            if (esCancelado) {
                                val motivo = p.motivoCancelacion ?: p.motivoCancelacionSnake ?: "Cancelado por el sistema o el usuario"
                                Text(
                                    text = "Motivo: $motivo",
                                    color = Color(0xFFD50000),
                                    fontSize = 12.sp,
                                    fontWeight = FontWeight.Bold,
                                    modifier = Modifier.padding(top = 4.dp)
                                )
                            }
                        }
                    }
                }

                Spacer(Modifier.height(16.dp))

                // --- INFO DEL RESTAURANTE Y ENTREGA ---
                val esMandadito = p.tipoPedido.uppercase() == "MANDADITO"
                
                if (esMandadito) {
                    // 📦 DISEÑO OPERATIVO DE DOBLE PUNTO (P2P)
                    Card(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                        colors = CardDefaults.cardColors(containerColor = colorFondoTarjetas),
                        shape = RoundedCornerShape(16.dp),
                        border = BorderStroke(1.dp, colorDivisor.copy(alpha = 0.5f))
                    ) {
                        Column(modifier = Modifier.padding(16.dp)) {
                            Text(
                                text = "Ruta del Envío", 
                                fontWeight = FontWeight.ExtraBold, 
                                fontSize = 14.sp, 
                                color = RosaYendo
                            )
                            Spacer(Modifier.height(16.dp))

                            // --- PUNTO A: PUNTO DE RECOLECCIÓN (ORIGEN) ---
                            Row(verticalAlignment = Alignment.Top) {
                                Column(horizontalAlignment = Alignment.CenterHorizontally, modifier = Modifier.padding(top = 2.dp)) {
                                    Icon(
                                        imageVector = Icons.Default.RadioButtonChecked, 
                                        contentDescription = null, 
                                        tint = Color(0xFF4CAF50), // Verde Origen
                                        modifier = Modifier.size(16.dp)
                                    )
                                    // Línea sutil conectora visual
                                    Box(modifier = Modifier.width(2.dp).height(34.dp).background(Color.LightGray.copy(alpha = 0.5f)))
                                }
                                Spacer(Modifier.width(12.dp))
                                Column {
                                    Text("Punto de Recolección", fontWeight = FontWeight.Bold, fontSize = 12.sp, color = colorTextoSecundario)
                                    Text(p.ubicacionNegocio.direccion, fontSize = 14.sp, color = colorTextoPrincipal, maxLines = 2, overflow = TextOverflow.Ellipsis)
                                    if (p.ubicacionNegocio.indicaciones.isNotBlank()) {
                                        Text("Ref: ${p.ubicacionNegocio.indicaciones}", fontSize = 12.sp, color = colorTextoSecundario)
                                    }
                                }
                            }

                            // --- PUNTO B: PUNTO DE ENTREGA (DESTINO) ---
                            Row(verticalAlignment = Alignment.Top) {
                                Icon(
                                    imageVector = Icons.Default.LocationOn, 
                                    contentDescription = null, 
                                    tint = RosaYendo, // Rosa Destino
                                    modifier = Modifier.size(16.dp).padding(top = 2.dp)
                                )
                                Spacer(Modifier.width(12.dp))
                                Column {
                                    Text("Punto de Entrega", fontWeight = FontWeight.Bold, fontSize = 12.sp, color = colorTextoSecundario)
                                    Text(p.ubicacionEntrega.direccion, fontSize = 14.sp, color = colorTextoPrincipal, maxLines = 2, overflow = TextOverflow.Ellipsis)
                                    if (p.ubicacionEntrega.indicaciones.isNotBlank()) {
                                        Text("Ref: ${p.ubicacionEntrega.indicaciones}", fontSize = 12.sp, color = colorTextoSecundario)
                                    }
                                }
                            }
                        }
                    }
                } else {
                    Card(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                        colors = CardDefaults.cardColors(containerColor = colorFondoTarjetas),
                        shape = RoundedCornerShape(12.dp)
                    ) {
                        Column(modifier = Modifier.padding(16.dp)) {
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.weight(1f)) {
                                    Box(
                                        modifier = Modifier
                                            .size(40.dp)
                                            .clip(CircleShape)
                                            .background(colorDivisor),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        if (p.logoRestaurante.isNotBlank()) {
                                            AsyncImage(
                                                model = p.logoRestaurante,
                                                contentDescription = "Logo",
                                                contentScale = ContentScale.Crop,
                                                modifier = Modifier.fillMaxSize()
                                            )
                                        } else {
                                            Icon(Icons.Default.Storefront, null, tint = colorTextoSecundario)
                                        }
                                    }
                                    Spacer(Modifier.width(12.dp))
                                    Column {
                                        Text(p.nombreRestaurante, fontWeight = FontWeight.Bold, color = colorTextoPrincipal)
                                        Text(formatFecha(p.tiempos.creado), color = colorTextoSecundario, fontSize = 12.sp)
                                    }
                                }

                                // 🚀 BADGE DE MODALIDAD (DELIVERY / PICKUP CON ICONO VECTORIAL)
                                val isDelivery = p.tipoPedido.uppercase() == "DELIVERY"
                                val tagBg = if (isDelivery) Color(0xFF2196F3).copy(alpha = 0.15f) else Color(0xFF4CAF50).copy(alpha = 0.15f)
                                val tagColor = if (isDelivery) Color(0xFF2196F3) else Color(0xFF4CAF50)

                                Surface(
                                    color = tagBg,
                                    shape = RoundedCornerShape(6.dp)
                                ) {
                                    Row(
                                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp),
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Icon(
                                            imageVector = if (isDelivery) Icons.Default.Moped else Icons.Default.ShoppingBag,
                                            contentDescription = null,
                                            tint = tagColor,
                                            modifier = Modifier.size(13.dp)
                                        )
                                        Spacer(Modifier.width(4.dp))
                                        Text(
                                            text = if (isDelivery) "Delivery" else "Pickup",
                                            color = tagColor,
                                            fontSize = 11.sp,
                                            fontWeight = FontWeight.Black
                                        )
                                    }
                                }
                            }
                            HorizontalDivider(modifier = Modifier.padding(vertical = 12.dp), color = colorDivisor)
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Box(modifier = Modifier.size(40.dp).clip(CircleShape).background(Color(0xFFE3F2FD)), contentAlignment = Alignment.Center) {
                                    Icon(Icons.Default.LocationOn, null, tint = Color(0xFF1976D2))
                                }
                                Spacer(Modifier.width(12.dp))
                                Column {
                                    Text("Entregar en:", color = colorTextoSecundario, fontSize = 12.sp)
                                    Text(p.ubicacionEntrega.direccion, fontWeight = FontWeight.Medium, color = colorTextoPrincipal, fontSize = 14.sp)
                                }
                            }
                        }
                    }
                }

                Spacer(Modifier.height(16.dp))

                // --- 🚀 RESUMEN DE ARTÍCULOS (CON AUDITORÍA DE BÁSCULA Y SUSTITUTOS) ---
                Text("Resumen del pedido", modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp), fontWeight = FontWeight.Bold, color = colorTextoPrincipal)
                
                Column(modifier = Modifier.padding(horizontal = 16.dp)) {
                    p.productos.forEachIndexed { prodIndex, prod ->
                        // 🚀 OBTENER EL PRODUCTO ORIGINAL SOLICITADO EN CHECKOUT CON COMPARACIÓN FIEL POR ÍNDICE Y OPCIONES
                        val prodOriginal = p.productosSolicitados.getOrNull(prodIndex)?.takeIf { orig ->
                            ((orig.id.isNotBlank() && orig.id == prod.id) || (orig.nombre.isNotBlank() && orig.nombre == prod.nombre)) &&
                            orig.notas.trim() == prod.notas.trim() &&
                            orig.adicionales.map { "${it.nombre_grupo}:${it.nombre}:${it.precioExtra}" }.sorted() ==
                            prod.adicionales.map { "${it.nombre_grupo}:${it.nombre}:${it.precioExtra}" }.sorted()
                        } ?: p.productosSolicitados.firstOrNull { orig ->
                            ((orig.id.isNotBlank() && orig.id == prod.id) || (orig.nombre.isNotBlank() && orig.nombre == prod.nombre)) &&
                            orig.notas.trim() == prod.notas.trim() &&
                            orig.adicionales.map { "${it.nombre_grupo}:${it.nombre}:${it.precioExtra}" }.sorted() ==
                            prod.adicionales.map { "${it.nombre_grupo}:${it.nombre}:${it.precioExtra}" }.sorted()
                        } ?: p.productosSolicitados.firstOrNull { orig ->
                            (orig.id.isNotBlank() && orig.id == prod.id) || (orig.nombre.isNotBlank() && orig.nombre == prod.nombre)
                        }

                        val cantOriginal = prodOriginal?.cantidad ?: prod.cantidad
                        val huboAjustePeso = prod.pesoRealKg != null
                        val huboAjusteCantidad = prodOriginal != null && (prod.cantidad != prodOriginal.cantidad)

                        val esAgotado = prod.cantidad == 0.0 && (prod.pesoRealKg == null || prod.pesoRealKg == 0.0) && !prod.agregadoEnPicking
                        val tieneSustitutoReal = !prod.sustitutoDe.isNullOrBlank()

                        val colorFondoCelda = when {
                            esAgotado -> if (isDark) Color(0xFF2C2C2C) else Color(0xFFF5F5F5)
                            tieneSustitutoReal -> if (isDark) Color(0xFF332B00) else Color(0xFFFFFDE7)
                            prod.agregadoEnPicking -> if (isDark) Color(0xFF1B3320) else Color(0xFFE8F5E9)
                            (huboAjustePeso || huboAjusteCantidad) -> if (isDark) Color(0xFF3A2312) else Color(0xFFFFF3E0)
                            else -> Color.Transparent
                        }

                        // 2. Cálculo de Precios Base vs Real
                        val pesoOriginal = when (prod.tipoVenta) {
                            "PIEZA_POR_PESO" -> cantOriginal * (prodOriginal?.pesoEstimadoKg ?: prod.pesoEstimadoKg)
                            "PESO" -> if (cantOriginal > 0) cantOriginal else prod.pesoEstimadoKg
                            else -> cantOriginal
                        }

                        // 🚀 MODELO A: PRECIO BASE EN CABECERA ($300) + EXTRAS EN ADICIONALES (+$150) = TOTAL ($450)
                        val subtotalOriginal = pesoOriginal * (prodOriginal?.precioUnitario ?: prod.precioUnitario)
                        val subtotalReal = when (prod.tipoVenta) {
                            "PIEZA_POR_PESO" -> (prod.pesoRealKg ?: (prod.cantidad * prod.pesoEstimadoKg)) * prod.precioUnitario
                            "PESO" -> (prod.pesoRealKg ?: prod.cantidad) * prod.precioUnitario
                            else -> prod.cantidad * prod.precioUnitario
                        }

                        val esModificado = (huboAjustePeso || huboAjusteCantidad || (subtotalReal != subtotalOriginal)) && !esAgotado && !prod.agregadoEnPicking

                        val colorItem = when {
                            esAgotado -> Color.Red
                            prod.agregadoEnPicking -> Color(0xFF2196F3) // Azul
                            esModificado -> Color(0xFFE65100) // Naranja
                            else -> colorTextoPrincipal
                        }
                        val colorSub = if (colorItem == colorTextoPrincipal) colorTextoSecundario else colorItem.copy(alpha = 0.8f)

                        // 3. Renderizado de la Celda
                        Surface(
                            modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp),
                            color = colorFondoCelda,
                            shape = RoundedCornerShape(12.dp)
                        ) {
                            Column(modifier = Modifier.padding(12.dp)) {
                                // BANNERS
                                if (tieneSustitutoReal && !esAgotado) {
                                    Text("Sustituye a: ${prod.sustitutoDe}", color = Color(0xFFFF9800), fontSize = 11.sp, fontWeight = FontWeight.Black, modifier = Modifier.padding(bottom = 6.dp))
                                } else if (prod.agregadoEnPicking && !esAgotado) {
                                    Text("Agregado extra por el comercio", color = Color(0xFF4CAF50), fontSize = 11.sp, fontWeight = FontWeight.Black, modifier = Modifier.padding(bottom = 6.dp))
                                } else if (huboAjusteCantidad && !esAgotado) {
                                    Text("Cantidad modificada por el comercio", color = Color(0xFFE65100), fontSize = 11.sp, fontWeight = FontWeight.Black, modifier = Modifier.padding(bottom = 6.dp))
                                }

                                // TEXTO CANTIDADES COMPARATIVO (CON FLECHAS ➔)
                                val textoCantidad = when (prod.tipoVenta) {
                                    "PIEZA_POR_PESO" -> {
                                        val piezasOrig = cantOriginal.toInt()
                                        val piezasNuevas = prod.cantidad.toInt()
                                        val txtPzas = if (piezasOrig != piezasNuevas) "$piezasOrig un. ➔ $piezasNuevas un." else "$piezasNuevas un."
                                        if (huboAjustePeso) {
                                            val pesoEst = cantOriginal * (prodOriginal?.pesoEstimadoKg ?: prod.pesoEstimadoKg)
                                            "$txtPzas (${formatPrecio(pesoEst)}kg ➔ ${prod.pesoRealKg}kg)"
                                        } else {
                                            "$txtPzas (${formatPrecio(prod.cantidad * prod.pesoEstimadoKg)}kg)"
                                        }
                                    }
                                    "PESO" -> {
                                        if (huboAjustePeso) {
                                            val original = if (cantOriginal > 0) cantOriginal else prod.pesoEstimadoKg
                                            "${formatPrecio(original)}kg ➔ ${formatPrecio(prod.pesoRealKg ?: 0.0)}kg"
                                        } else if (huboAjusteCantidad) {
                                            "${formatPrecio(cantOriginal)}kg ➔ ${formatPrecio(prod.cantidad)}kg"
                                        } else {
                                            "${formatPrecio(prod.cantidad)}kg"
                                        }
                                    }
                                    else -> {
                                        val cantOrigStr = if (cantOriginal % 1.0 == 0.0) "${cantOriginal.toInt()}" else formatPrecio(cantOriginal)
                                        val cantNuevaStr = if (prod.cantidad % 1.0 == 0.0) "${prod.cantidad.toInt()}" else formatPrecio(prod.cantidad)
                                        if (huboAjusteCantidad) {
                                            "$cantOrigStr un. ➔ $cantNuevaStr un."
                                        } else {
                                            "$cantNuevaStr un."
                                        }
                                    }
                                }

                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Column(modifier = Modifier.weight(1f)) {
                                        Text(text = prod.nombre, fontWeight = FontWeight.Bold, fontSize = 14.sp, color = colorItem)
                                        Text(text = if (esAgotado) "No disponible en tienda" else textoCantidad, fontSize = 12.sp, color = colorSub)
                                    }
                                    
                                    Column(horizontalAlignment = Alignment.End) {
                                        if (esModificado && subtotalOriginal > 0) {
                                            Text(text = "$${formatPrecio(subtotalOriginal)}", fontSize = 11.sp, color = colorTextoSecundario, textDecoration = TextDecoration.LineThrough)
                                        }
                                        if (esAgotado) {
                                            Text("AGOTADO", color = Color.Red, fontSize = 12.sp, fontWeight = FontWeight.Bold, textDecoration = TextDecoration.LineThrough)
                                        } else {
                                            Text("$${formatPrecio(subtotalReal)}", fontWeight = FontWeight.ExtraBold, fontSize = 15.sp, color = colorItem)
                                        }
                                    }
                                }

                                // 🚀 RENDERIZADO DE ADICIONALES (EXTRAS / PERSONALIZACIÓN EN HISTORIAL DE PEDIDOS)
                                if (prod.adicionales.isNotEmpty()) {
                                    Column(modifier = Modifier.padding(top = 6.dp)) {
                                        prod.adicionales.forEach { adic ->
                                            Row(
                                                modifier = Modifier.fillMaxWidth().padding(vertical = 1.dp),
                                                horizontalArrangement = Arrangement.SpaceBetween
                                            ) {
                                                Text(
                                                    text = "+ ${adic.nombre}${if (adic.nombre_grupo.isNotBlank()) " (${adic.nombre_grupo})" else ""}",
                                                    fontSize = 12.sp,
                                                    color = if (isDark) Color.LightGray else Color.Black,
                                                    fontWeight = FontWeight.Medium
                                                )
                                                if (adic.precioExtra > 0) {
                                                    val totalExtraAdic = adic.precioExtra * prod.cantidad
                                                    Text(
                                                        text = "+$${formatPrecio(totalExtraAdic)}",
                                                        fontSize = 12.sp,
                                                        color = RosaYendo,
                                                        fontWeight = FontWeight.Bold
                                                    )
                                                }
                                            }
                                        }
                                    }
                                }

                                // 🚀 RENDERIZADO DE NOTAS / INSTRUCCIONES ESPECIALES DEL CLIENTE
                                if (prod.notas.isNotBlank()) {
                                    Surface(
                                        color = Color(0xFFFF9800).copy(alpha = 0.12f),
                                        shape = RoundedCornerShape(6.dp),
                                        modifier = Modifier.padding(top = 6.dp)
                                    ) {
                                        Text(
                                            text = "📝 Nota: \"${prod.notas}\"",
                                            fontSize = 12.sp,
                                            color = Color(0xFFFF9800),
                                            fontWeight = FontWeight.Bold,
                                            modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp)
                                        )
                                    }
                                }
                            }
                        }
                    }
                }

                Spacer(Modifier.height(16.dp))

                // --- MÉTODO DE PAGO ---
                Card(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                    colors = CardDefaults.cardColors(containerColor = colorFondoTarjetas),
                    shape = RoundedCornerShape(12.dp)
                ) {
                    Row(modifier = Modifier.padding(16.dp), verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Default.Payment, null, tint = colorTextoSecundario)
                        Spacer(Modifier.width(12.dp))
                        Column {
                            Text("Método de pago", color = colorTextoSecundario, fontSize = 12.sp)
                            Text(p.metodoPago, fontWeight = FontWeight.Bold, color = colorTextoPrincipal)
                        }
                    }
                }

                Spacer(Modifier.height(16.dp))

                // --- TOTALES REALES ---
                val totalOriginalEstimado = p.desgloseCliente.totalPagado
                val totalNuevoAjustado = p.desgloseCliente.totalPagadoFinal 
                    ?: p.desgloseCliente.totalPagadoFinalSnake 
                    ?: p.totalPagadoFinal 
                    ?: p.totalPagadoFinalSnake

                val hayCambioTotal = totalNuevoAjustado != null && kotlin.math.abs(totalNuevoAjustado - totalOriginalEstimado) > 0.01

                val subtotalAjustado = p.desgloseCliente.subtotalProductosActual
                val subtotalOriginal = p.desgloseCliente.subtotalOriginalActual ?: subtotalAjustado
                val hayCambioSubtotal = hayCambioTotal && kotlin.math.abs(subtotalAjustado - subtotalOriginal) > 0.01

                Card(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                    colors = CardDefaults.cardColors(containerColor = colorFondoTarjetas),
                    shape = RoundedCornerShape(12.dp)
                ) {
                    Column(modifier = Modifier.padding(16.dp)) {
                        FilaTotalComparativa(
                            etiqueta = "Subtotal de productos",
                            montoOriginal = if (hayCambioSubtotal) subtotalOriginal else null,
                            montoNuevo = subtotalAjustado,
                            colorEtiqueta = colorTextoSecundario,
                            colorMonto = colorTextoPrincipal
                        )
                        FilaTotal("Envío", p.desgloseCliente.costoEnvio, colorTextoSecundario, colorTextoPrincipal)
                        if (p.desgloseCliente.propina > 0) {
                            FilaTotal("Propina", p.desgloseCliente.propina, colorTextoSecundario, colorTextoPrincipal)
                        }
                        if (p.desgloseCliente.tarifaServicio > 0) {
                            FilaTotal("Tarifa de servicio", p.desgloseCliente.tarifaServicio, colorTextoSecundario, colorTextoPrincipal)
                        }
                        if (p.desgloseCliente.descuentoCupon > 0) {
                            FilaTotal("Descuento cupón", -p.desgloseCliente.descuentoCupon, Color(0xFF00C853), Color(0xFF00C853))
                        }

                        HorizontalDivider(modifier = Modifier.padding(vertical = 12.dp), color = colorDivisor)

                        Row(
                            modifier = Modifier.fillMaxWidth(), 
                            horizontalArrangement = Arrangement.SpaceBetween, 
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text("Total", fontWeight = FontWeight.Black, fontSize = 16.sp, color = colorTextoPrincipal)
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                if (hayCambioTotal) {
                                    Text(
                                        text = "$${formatPrecio(totalOriginalEstimado)}",
                                        color = colorTextoSecundario,
                                        fontSize = 12.sp,
                                        textDecoration = TextDecoration.LineThrough
                                    )
                                    Spacer(Modifier.width(6.dp))
                                    Text(
                                        text = "$${formatPrecio(totalNuevoAjustado ?: totalOriginalEstimado)}",
                                        fontWeight = FontWeight.ExtraBold,
                                        fontSize = 20.sp,
                                        color = RosaYendo
                                    )
                                } else {
                                    Text(
                                        text = "$${formatPrecio(p.desgloseCliente.totalPagado)}",
                                        fontWeight = FontWeight.ExtraBold,
                                        fontSize = 20.sp,
                                        color = RosaYendo
                                    )
                                }
                            }
                        }
                    }
                }

                Spacer(Modifier.height(16.dp))

                // --- 🚀 DETALLES DE TIEMPO COMPLETOS DE LA ORDEN ---
                Card(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                    colors = CardDefaults.cardColors(containerColor = colorFondoTarjetas),
                    shape = RoundedCornerShape(12.dp)
                ) {
                    Column(modifier = Modifier.padding(16.dp)) {
                        Text("Detalles de tiempo", fontWeight = FontWeight.Bold, color = colorTextoPrincipal, fontSize = 14.sp)
                        Spacer(Modifier.height(8.dp))

                        FilaTiempo("Solicitado", p.tiempos.creado, colorTextoSecundario, colorTextoPrincipal)

                        if (p.tiempos.aceptado != null) {
                            FilaTiempo("Aceptado por el restaurante", p.tiempos.aceptado, colorTextoSecundario, colorTextoPrincipal)
                        }
                        if (p.tiempos.recolectado != null) {
                            FilaTiempo("Recogido en mostrador", p.tiempos.recolectado, colorTextoSecundario, colorTextoPrincipal)
                        }
                        val tEnCamino = p.tiempos.enCaminoFinal
                        if (tEnCamino != null) {
                            FilaTiempo("En camino al cliente", tEnCamino, colorTextoSecundario, colorTextoPrincipal)
                        }
                        val tEnPuerta = p.tiempos.enPuertaFinal
                        if (tEnPuerta != null) {
                            FilaTiempo("Llegada a domicilio (En puerta)", tEnPuerta, colorTextoSecundario, colorTextoPrincipal)
                        }
                        val tConcluido = p.tiempos.concluidoFinal
                        if (tConcluido != null) {
                            FilaTiempo("Entregado", tConcluido, colorTextoSecundario, colorTextoPrincipal)
                        }
                    }
                }

                Spacer(Modifier.height(80.dp))
            }
        }
    }
}


@Composable
fun FilaTotalComparativa(
    etiqueta: String, 
    montoOriginal: Double?, 
    montoNuevo: Double, 
    colorEtiqueta: Color, 
    colorMonto: Color
) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp), 
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(etiqueta, color = colorEtiqueta, fontSize = 14.sp)
        Row(verticalAlignment = Alignment.CenterVertically) {
            if (montoOriginal != null && kotlin.math.abs(montoNuevo - montoOriginal) > 0.01) {
                Text(
                    text = "$${formatPrecio(montoOriginal)}",
                    color = colorEtiqueta,
                    fontSize = 11.sp,
                    textDecoration = TextDecoration.LineThrough
                )
                Spacer(Modifier.width(6.dp))
            }
            Text("$${formatPrecio(montoNuevo)}", fontSize = 14.sp, color = colorMonto)
        }
    }
}

@Composable
fun FilaTotal(etiqueta: String, monto: Double, colorEtiqueta: Color, colorMonto: Color) {
    Row(modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp), horizontalArrangement = Arrangement.SpaceBetween) {
        Text(etiqueta, color = colorEtiqueta, fontSize = 14.sp)
        Text("$${formatPrecio(monto)}", fontSize = 14.sp, color = colorMonto)
    }
}

// 🚀 Nueva fila para mostrar los tiempos de manera limpia
@Composable
fun FilaTiempo(etiqueta: String, epoch: Long, colorEtiqueta: Color, colorHora: Color) {
    Row(modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp), horizontalArrangement = Arrangement.SpaceBetween) {
        Text(etiqueta, color = colorEtiqueta, fontSize = 13.sp)
        Text(formatHoraSencilla(epoch), fontSize = 13.sp, color = colorHora, fontWeight = FontWeight.Medium)
    }
}

// Función auxiliar solo para sacar la pura hora en formato HH:MM
fun formatHoraSencilla(epoch: Long): String {
    return try {
        val instant = Instant.fromEpochMilliseconds(epoch)
        val dateTime = instant.toLocalDateTime(TimeZone.currentSystemDefault())
        val hora = dateTime.hour.toString().padStart(2, '0')
        val min = dateTime.minute.toString().padStart(2, '0')
        "$hora:$min"
    } catch (e: Exception) { "--:--" }
}